import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  InputGroup,
  InputGroupText,
  FormGroup,
} from "reactstrap";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { get, post, put } from "helpers/api_helper";
import {
  GET_ALL_COUNT_VENDORS,
  GET_ALL_VENDORS,
  UPDATE_VENDOR_DATA_BY_ID,
} from "constants/api_constants";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { formatToPascal } from "helpers/utils/nameUtils";

function Vendors() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [vendorsData, setVendorsData] = useState([]);
  const [vendorsCount, setVendorsCount] = useState({
    totalCount: 0,
    activeVendors: 0,
    inactiveVendors: 0,
  });
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "vendorId",
    direction: "DESC",
  });
  const timerRef = useRef(null);

  // Fetch vendor counts
  const fetchVendorCount = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const response = await get(GET_ALL_COUNT_VENDORS);
      console.log("fetchVendorCount response", response);
      const countData = response?.data || response;
      setVendorsCount({
        totalCount: parseInt(countData.totalCount) || 0,
        activeVendors: parseInt(countData.activeVendors) || 0,
        inactiveVendors: parseInt(countData.inactiveVendors) || 0,
      });
      setApiStatus((prev) => ({ ...prev, loading: false, error: "" }));
    } catch (error) {
      console.error("fetchVendorCount error", error);
      setVendorsCount({ totalCount: 0, activeVendors: 0, inactiveVendors: 0 });
      setApiStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || "Failed to fetch vendor counts",
      }));
    }
  }, []);

  // Fetch vendors with search, pagination, and sorting
  const fetchVendors = useCallback(
    async (query = "", page = 1, sortBy = sortConfig.sortBy, direction = sortConfig.direction) => {
      try {
        setApiStatus((prev) => ({ ...prev, loading: true }));
        const payload = {
          searchKey: query.trim() || "",
          page: page - 1, // 0-based indexing
          pageSize: 10,
          sortBy: sortBy,
          direction: direction,
        };
        console.log("fetchVendors payload", payload);
        const response = await post(GET_ALL_VENDORS, payload);
        console.log("fetchVendors response", response);

        // Handle response
        const data = response?.data?.data || [];
        const totalPages = response?.data?.totalPages || (data.length > 0 ? 1 : 0);

        // Log the data to verify sorting
        console.log("Sorted vendors data:", data);

        setVendorsData(Array.isArray(data) ? data : []);
        setTotalPages(Number(totalPages) || 0);
        setApiStatus((prev) => ({ ...prev, loading: false, error: "" }));
      } catch (error) {
        console.error("fetchVendors error", error);
        setVendorsData([]);
        setTotalPages(0);
        setApiStatus((prev) => ({
          ...prev,
          loading: false,
          error:
            error.response?.data?.message ||
            (query ? "No vendors found for this query" : "Failed to fetch vendors"),
        }));
      }
    },
    []
  );

  // Handle sort
  const handleSort = useCallback(
    (columnName) => {
      setSortConfig((prevConfig) => {
        const newConfig = {
          sortBy: columnName,
          direction:
            prevConfig.sortBy === columnName && prevConfig.direction === "ASC"
              ? "DESC"
              : "ASC",
        };
        console.log("New sort config:", newConfig);
        // Trigger fetch with new sort config
        fetchVendors(searchQuery, currentPage, newConfig.sortBy, newConfig.direction);
        return newConfig;
      });
    },
    [fetchVendors, searchQuery, currentPage]
  );

  // Sort arrows component
  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "ASC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "DESC"
              ? "text-primary"
              : "text-muted"
          }`}
        />
      </span>
    );
  };

  // Mapping of table columns to API sort keys
  const columnSortMap = {
    "Vendor Code": "vendorCode",
    Company: "companyName",
    "Primary Contact": "primaryContactName",
    Email: "email",
    Designation: "designation",
    Status: "status",
  };

  // Render table header with sorting
  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Vendor Code",
          "Company",
          "Primary Contact",
          "Email",
          "Designation",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows
                column={columnSortMap[header]}
                currentSort={sortConfig}
              />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  // Fetch data on mount and when searchQuery or currentPage changes
  useEffect(() => {
    fetchVendorCount();
    fetchVendors(searchQuery, currentPage, sortConfig.sortBy, sortConfig.direction);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [fetchVendorCount, fetchVendors, searchQuery, currentPage, sortConfig.sortBy, sortConfig.direction]);

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchVendors(searchQuery, page, sortConfig.sortBy, sortConfig.direction);
    },
    [fetchVendors, searchQuery, sortConfig]
  );

  // Handle search query with debounce
  const handleSearchQuery = useCallback(
    (e) => {
      const query = e.target.value || "";
      setSearchQuery(query);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setCurrentPage(1);
        fetchVendors(query.trim(), 1, sortConfig.sortBy, sortConfig.direction);
      }, 300);
    },
    [fetchVendors, sortConfig]
  );

  // Toggle vendor status
  const toggleVendorStatus = useCallback(
    async (vendorId) => {
      const vendorData = vendorsData.find((v) => v.vendorId === vendorId);
      if (vendorData) {
        try {
          setApiStatus((prev) => ({ ...prev, loading: true }));
          const updatedVendor = {
            vendorDto: {
              vendorId: vendorData.vendorId,
              status: vendorData.status === 1 ? 0 : 1,
            },
          };
          console.log("toggleVendorStatus payload", updatedVendor);
          const response = await put(UPDATE_VENDOR_DATA_BY_ID(vendorId), updatedVendor);
          console.log("toggleVendorStatus response", response);

          if (response.status !== "/Success" && response.code !== 200) {
            throw new Error(response.message || "Failed to update vendor status");
          }

          showSuccessToast("Vendor status updated successfully");
          await Promise.all([
            fetchVendors(searchQuery, currentPage, sortConfig.sortBy, sortConfig.direction),
            fetchVendorCount(),
          ]);
          setApiStatus((prev) => ({ ...prev, loading: false, error: "" }));
        } catch (error) {
          console.error("toggleVendorStatus error", error);
          showErrorToast(error.response?.data?.message || "Failed to update vendor status");
          setApiStatus((prev) => ({
            ...prev,
            loading: false,
            error: error.response?.data?.message || "Failed to update vendor status",
          }));
        }
      }
    },
    [fetchVendors, fetchVendorCount, searchQuery, currentPage, sortConfig, vendorsData]
  );

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Vendor Code",
      "Company",
      "Primary Contact",
      "Email",
      "Designation",
      "Status",
    ];
    const rows = vendorsData.map((vendor) => [
      vendor.vendorCode || "",
      vendor.companyName || "",
      vendor.primaryContactName || "",
      vendor.email || "",
      formatToPascal(vendor.designation || ""),
      vendor.status === 1 ? "Active" : "Inactive",
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "vendors.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Summary data for cards
  const summaryData = useMemo(
    () => [
      {
        iconClass: "fas fa-users text-primary",
        label: "Total Vendors",
        value: vendorsCount.totalCount || 0,
        countClass: "text-blue",
      },
      {
        iconClass: "fas fa-user-check text-primary",
        label: "Active Vendors",
        value: vendorsCount.activeVendors || 0,
        countClass: "text-primary",
      },
      {
        iconClass: "fas fa-user-times text-danger",
        label: "Inactive Vendors",
        value: vendorsCount.inactiveVendors || 0,
        countClass: "text-blue",
      },
    ],
    [vendorsCount]
  );

  return (
    <div className="page-content">
      <Container fluid={true} style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading={"Vendors Summary"}
          routePath={"Home"}
          pageName={"Vendors"}
        />
        <Row className="px-4">
          <SummaryCards data={summaryData} />

          {/* Search and Export Row */}
          <Row className="align-items-center row-gap-3 justify-content-between py-2">
            <Col md={4}>
              <InputGroup size="sm" className="elbert-search-group">
                <Input
                  type="text"
                  placeholder="Search by vendor code, company, or contact"
                  value={searchQuery}
                  onChange={handleSearchQuery}
                  className="elbert-search"
                  aria-label="Search vendors"
                />
                <InputGroupText>
                  <span className="fa fa-search"></span>
                </InputGroupText>
              </InputGroup>
            </Col>
            <Col className="d-flex gap-3 align-items-center justify-content-end">
              <Button
                color="primary"
                outline
                className="waves-effect waves-light"
                onClick={exportToCSV}
                disabled={apiStatus.loading || vendorsData.length === 0}
              >
                Export CSV
              </Button>
              <CustomButton
                text={"+ Add New"}
                onClick={() => navigate("/add-vendor")}
              />
            </Col>
          </Row>

          {/* Vendors Table */}
          <Row className="py-2">
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table mb-0">
                      <colgroup>
                        <col style={{ width: "12%" }} /> {/* Vendor Code */}
                        <col style={{ width: "20%" }} /> {/* Company */}
                        <col style={{ width: "18%" }} /> {/* Primary Contact */}
                        <col style={{ width: "20%" }} /> {/* Email */}
                        <col style={{ width: "12%" }} /> {/* Designation */}
                        <col style={{ width: "8%" }} /> {/* Status */}
                        <col style={{ width: "10%" }} /> {/* Actions */}
                      </colgroup>
                      {renderTableHeader()}
                      <tbody>
                        {apiStatus.loading ? (
                          <tr>
                            <td colSpan="7" className="text-center">
                              Loading...
                            </td>
                          </tr>
                        ) : apiStatus.error ? (
                          <tr>
                            <td colSpan="7" className="text-danger text-center">
                              {apiStatus.error}
                              <button
                                onClick={() => {
                                  fetchVendors(searchQuery, currentPage, sortConfig.sortBy, sortConfig.direction);
                                  fetchVendorCount();
                                }}
                                className="btn btn-link"
                              >
                                Retry
                              </button>
                            </td>
                          </tr>
                        ) : vendorsData.length > 0 ? (
                          vendorsData.map((vendor, index) => (
                            <tr key={vendor.vendorId || index}>
                              <td>{vendor.vendorCode || "N/A"}</td>
                              <td>{vendor.companyName || "N/A"}</td>
                              <td>{vendor.primaryContactName || "N/A"}</td>
                              <td>{vendor.email || "N/A"}</td>
                              <td>{formatToPascal(vendor.designation || "N/A")}</td>
                              <td>
                                <FormGroup switch>
                                  <Input
                                    style={{
                                      color: "blue",
                                      height: "1.4em",
                                      width: "3em",
                                      cursor: "pointer",
                                    }}
                                    type="switch"
                                    disabled={apiStatus.loading}
                                    id={`vendorStatus-${vendor.vendorId}`}
                                    checked={vendor.status === 1}
                                    onChange={() => toggleVendorStatus(vendor.vendorId)}
                                    aria-label={`Toggle status for vendor ${vendor.vendorCode || "N/A"}`}
                                  />
                                </FormGroup>
                              </td>
                              <td className="text-center">
                                <div className="d-flex gap-3 align-items-center justify-content-center">
                                  <i
                                    onClick={() => navigate(`/view-vendor/${vendor.vendorId}`)}
                                    className="fa-solid fa-eye action-icon"
                                    title="View"
                                    role="button"
                                    aria-label={`View vendor ${vendor.vendorCode || "N/A"}`}
                                  ></i>
                                  <i
                                    onClick={() => navigate(`/add-vendor/${vendor.vendorId}`)}
                                    className="fa-regular fa-pen-to-square action-icon"
                                    title="Edit"
                                    role="button"
                                    aria-label={`Edit vendor ${vendor.vendorCode || "N/A"}`}
                                  ></i>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center">
                              No vendors found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Pagination */}
          {totalPages > 1 && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Row>
      </Container>
    </div>
  );
}

export default Vendors;